import React from 'react';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';
import { Body5, Container, H5 } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';
import { formatKeyName } from './templateEditorUtils';
import {
  AddCellFormWrapper,
  AddCellInputRowWrapper,
  CellInputFieldWrapper,
  CellWrapper,
  DeleteButtonWrapper,
  InputField,
  SaveButtonWrapper,
} from './templateActionStyles';

function Cell({ templateId, cell, onDelete }) {
  const [isSubmittingToApi, setIsSubmittingToApi] = React.useState(false);
  const [message, setMessage] = React.useState(null);
  const [formError, setFormError] = React.useState(null);
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const [cellValue, setCellValue] = React.useState(cell);
  const [cellKeys, setCellKeys] = React.useState([]);
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    const omittedKeys = ['id'];
    const keys = Object.keys(cell || {}).filter((key) => !omittedKeys.includes(key));
    setCellKeys(keys);
    setCellValue(cell);
  }, [cell]);

  const handleCellChange = (key, value) => {
    setCellValue((currentCellValue) => ({
      ...currentCellValue,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    setIsSubmittingToApi(true);
    setFormError(null);
    setMessage(null);

    const url = `v1/genai/templates/${templateId}/cells/${cell.id}`;
    const { status, data, error } = await sendApiRequest('patch', url, cellValue);
    handle400Statuses(status);
    logNetRequest(url, status, data, error);
    setIsSubmittingToApi(false);

    if (status === 200) {
      setMessage('Cell saved successfully');
    } else if (status === 403) {
      setFormError('You are not authorized to edit this cell');
    } else {
      setFormError(error || 'Something went wrong, try again');
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setFormError(null);
    setMessage(null);

    const url = `v1/genai/templates/${templateId}/cells/${cell.id}`;
    const { status, data, error } = await sendApiRequest('delete', url);
    handle400Statuses(status);
    logNetRequest(url, status, data, error);
    setIsDeleting(false);

    if (status === 200) {
      onDelete(cell.id);
      return;
    }

    if (status === 403) {
      setFormError('You are not authorized to delete this cell');
      return;
    }

    setFormError(error || 'Something went wrong, try again');
  };

  if (isCollapsed) {
    return (
      <CellWrapper onClick={() => setIsCollapsed(false)}>
        <Body5>
          Cell: <Body5 bold>{cell.cell_reference}</Body5> <Body5 bold>{cell.value || cell.cell_value}</Body5>
        </Body5>
      </CellWrapper>
    );
  }

  return (
    <CellWrapper>
      <div onClick={() => setIsCollapsed(true)}>
        <Body5>
          Cell: <Body5 bold>{cell.cell_reference}</Body5> <Body5 bold>{cell.value || cell.cell_value}</Body5>
        </Body5>
      </div>
      <Container top={16}>
        {cellKeys.map((key) => (
          <CellInputFieldWrapper key={key}>
            <Container right={8} style={{ width: '240px' }}>
              <Body5 bold>{formatKeyName(key)}</Body5>
            </Container>
            <InputField value={cellValue[key] || ''} onChange={(e) => handleCellChange(key, e.target.value)} />
          </CellInputFieldWrapper>
        ))}
      </Container>
      {message && (
        <Container top={16}>
          <Body5>{message}</Body5>
        </Container>
      )}
      {formError && (
        <Container top={16}>
          <ErrorText>{formError}</ErrorText>
        </Container>
      )}
      <Container top={16} row spaceBetween fullWidth>
        <SaveButtonWrapper>
          <FlatButton disabled={isSubmittingToApi || isDeleting} onClick={handleSave}>
            {isSubmittingToApi ? 'Saving...' : 'Save'}
          </FlatButton>
        </SaveButtonWrapper>
        <DeleteButtonWrapper>
          <FlatButton disabled={isDeleting || isSubmittingToApi} onClick={handleDelete}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </FlatButton>
        </DeleteButtonWrapper>
      </Container>
    </CellWrapper>
  );
}

function AddCellForm({ template, onComplete, onCancel }) {
  const [cell, setCell] = React.useState({
    cell_reference: '',
    cell_value: '',
    query_label: '',
    query_augmented_label: '',
    context_prompt_label: '',
    context_prompt_augmented_label: '',
    query_param_set: '',
  });
  const [formError, setFormError] = React.useState(null);
  const [isSubmittingToApi, setIsSubmittingToApi] = React.useState(false);

  const handleCellChange = (key, value) => {
    setCell((currentCell) => ({
      ...currentCell,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    setIsSubmittingToApi(true);
    setFormError(null);

    const url = `v1/genai/templates/${template.id}/cells`;
    const { status, data, error } = await sendApiRequest('post', url, cell);
    handle400Statuses(status);
    logNetRequest(url, status, data, error);
    setIsSubmittingToApi(false);

    if (status === 200) {
      onComplete(data);
      return;
    }

    setFormError(error || 'Something went wrong, try again');
  };

  return (
    <Container vertical={16}>
      <H5>Add Cell</H5>
      <Container top={16}>
        <AddCellInputRowWrapper>
          <Body5>Cell Reference</Body5>
          <InputField
            value={cell.cell_reference}
            onChange={(e) => handleCellChange('cell_reference', e.target.value)}
          />
        </AddCellInputRowWrapper>
        <AddCellInputRowWrapper>
          <Body5>Cell Value</Body5>
          <InputField value={cell.cell_value} onChange={(e) => handleCellChange('cell_value', e.target.value)} />
        </AddCellInputRowWrapper>
        <AddCellInputRowWrapper>
          <Body5>Query Label</Body5>
          <InputField value={cell.query_label} onChange={(e) => handleCellChange('query_label', e.target.value)} />
        </AddCellInputRowWrapper>
        <AddCellInputRowWrapper>
          <Body5>Query Augmented Label</Body5>
          <InputField
            value={cell.query_augmented_label}
            onChange={(e) => handleCellChange('query_augmented_label', e.target.value)}
          />
        </AddCellInputRowWrapper>
        <AddCellInputRowWrapper>
          <Body5>Context Prompt Label</Body5>
          <InputField
            value={cell.context_prompt_label}
            onChange={(e) => handleCellChange('context_prompt_label', e.target.value)}
          />
        </AddCellInputRowWrapper>
        <AddCellInputRowWrapper>
          <Body5>Context Prompt Augmented Label</Body5>
          <InputField
            value={cell.context_prompt_augmented_label}
            onChange={(e) => handleCellChange('context_prompt_augmented_label', e.target.value)}
          />
        </AddCellInputRowWrapper>
        <AddCellInputRowWrapper>
          <Body5>Query Param Set</Body5>
          <InputField
            value={cell.query_param_set}
            onChange={(e) => handleCellChange('query_param_set', e.target.value)}
          />
        </AddCellInputRowWrapper>
      </Container>
      <Container top={36}>
        {formError && (
          <Container bottom={16}>
            <ErrorText>{formError}</ErrorText>
          </Container>
        )}
        <Container row style={{ gap: '16px' }}>
          <SaveButtonWrapper>
            <FlatButton disabled={isSubmittingToApi} onClick={handleSave}>
              {isSubmittingToApi ? 'Saving...' : 'Save'}
            </FlatButton>
          </SaveButtonWrapper>
          <TransparentFlatButton onClick={onCancel}>Cancel</TransparentFlatButton>
        </Container>
      </Container>
    </Container>
  );
}

export function CellsEditor({ template, onCancel }) {
  const [cells, setCells] = React.useState(null);
  const [formError, setFormError] = React.useState(null);
  const [showAddCellForm, setShowAddCellForm] = React.useState(false);

  React.useEffect(() => {
    const cancelRef = { isCanceled: false };

    const fetchCells = async () => {
      const url = `v1/genai/templates/${template.id}/cells`;
      const { status, data, error } = await sendApiRequest('get', url);
      handle400Statuses(status);
      logNetRequest(url, status, data, error);

      if (cancelRef.isCanceled) {
        return;
      }

      if (status === 200) {
        setCells(data.cells);
      } else {
        setFormError(error || 'Something went wrong, try again');
      }
    };

    fetchCells();

    return () => {
      cancelRef.isCanceled = true;
    };
  }, [template]);

  const handleAddCellComplete = (cell) => {
    setCells((currentCells) => [...(currentCells || []), cell]);
    setShowAddCellForm(false);
  };

  const handleDeleteCell = (cellId) => {
    setCells((currentCells) => currentCells.filter((cell) => cell.id !== cellId));
  };

  return (
    <Container top={16}>
      <Container row spaceBetween fullWidth verticallyCenter>
        <Body5 bold>Cells</Body5>
        {!showAddCellForm && <FlatButton onClick={() => setShowAddCellForm(true)}>Add Cell</FlatButton>}
      </Container>
      {formError && (
        <Container top={16}>
          <Body5>{formError}</Body5>
        </Container>
      )}
      {showAddCellForm && (
        <AddCellFormWrapper>
          <AddCellForm
            template={template}
            onComplete={handleAddCellComplete}
            onCancel={() => setShowAddCellForm(false)}
          />
        </AddCellFormWrapper>
      )}
      <Container top={16}>
        {cells?.map((cell) => (
          <Cell key={cell.id} cell={cell} templateId={template.id} onDelete={handleDeleteCell} />
        ))}
      </Container>
      <TransparentFlatButton onClick={onCancel}>Cancel</TransparentFlatButton>
    </Container>
  );
}
